import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { toast } from '@/components';
import { KeyHint } from '@/components/KeyHint';
import { MultiSelect } from '@/components/MultiSelect';
import { TextField } from '@/components/TextField';
import { Modal, ModalVariant } from '@/components/Modal';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { Person, UpdatePersonPayload } from '@/types/person';
import type { ModalMode } from '@/features/DirectoryGrid';
import {
    ProfilePictureSection,
    type ProfilePicturePending,
} from './ProfilePictureSection';

interface UpdatePersonModalProps {
    isOpen: boolean;
    onClose: () => void;
    isSubmitting?: boolean;
    mode: ModalMode;
    person?: Person | null;
    onUpdate: (
        personId: string,
        payload: UpdatePersonPayload,
    ) => Promise<boolean>;
}

export function UpdatePersonModal({
    isOpen,
    onClose,
    isSubmitting = false,
    mode,
    person,
    onUpdate,
}: UpdatePersonModalProps) {
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.COMMON,
    ]);
    const [name, setName] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [profilePicturePending, setProfilePicturePending] =
        useState<ProfilePicturePending | null>(null);
    const [isApplyingPhoto, setIsApplyingPhoto] = useState(false);
    const [isPhotoFlowInProgress, setIsPhotoFlowInProgress] = useState(false);
    const { data: tagOptions = [] } = useSWR(
        isOpen ? CACHE_KEYS.ALL_PERSONS_TAGS : null,
        () => EngineService.getPersonTags(),
    );

    const multiSelectOptions = useMemo(
        () =>
            Array.from(new Set([...tagOptions, ...tags])).map((tag) => ({
                value: tag,
                label: tag,
            })),
        [tagOptions, tags],
    );

    useEffect(() => {
        if (isOpen && person) {
            setName(person.name || '');
            setTags(person.tags ?? []);
            setProfilePicturePending(null);
            setIsPhotoFlowInProgress(false);
        }
    }, [isOpen, person]);

    const isSubmitDisabled =
        isSubmitting ||
        isApplyingPhoto ||
        isPhotoFlowInProgress ||
        !name.trim();

    const applyProfilePicture = async () => {
        if (!person || !profilePicturePending) return;
        if (profilePicturePending.type === 'set') {
            await EngineService.setPersonProfilePicture(
                person.id,
                profilePicturePending.pendingId,
                profilePicturePending.crop,
            );
        } else {
            await EngineService.removePersonProfilePicture(person.id);
        }
    };

    const handleSubmit = async () => {
        if (!person || !name.trim()) return;
        if (profilePicturePending) {
            setIsApplyingPhoto(true);
            try {
                await applyProfilePicture();
                setProfilePicturePending(null);
            } catch {
                toast.error(t('persons.profilePicture.saveError'));
                return;
            } finally {
                setIsApplyingPhoto(false);
            }
        }
        const updated = await onUpdate(person.id, {
            name: name.trim(),
            tags,
        });
        if (!updated) return;
        onClose();
    };

    const handleClose = () => {
        setName('');
        setTags([]);
        setProfilePicturePending(null);
        setIsPhotoFlowInProgress(false);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={
                mode === 'recognize'
                    ? t('persons.recognizeModal.title')
                    : t('persons.editModal.title')
            }
            onSubmit={() => void handleSubmit()}
            submitText={
                mode === 'recognize'
                    ? t('persons.recognizeModal.submit')
                    : t('persons.editModal.submit')
            }
            submitDisabled={isSubmitDisabled}
            submitInProgress={isSubmitting || isApplyingPhoto}
            variant={ModalVariant.PLAIN}
            size="medium"
            maxHeight="max-h-[calc(100dvh-2rem)]"
            submitButtonTrailingIcon={<KeyHint size="enter">↵</KeyHint>}
            submitButtonFullWidth
        >
            <div className="flex flex-col gap-6 px-6 pb-8">
                <TextField
                    label={t('persons.editModal.name')}
                    labelClassName="text-sm-medium text-white-100"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('persons.editModal.namePlaceholder')}
                    autoFocus
                />
                <MultiSelect
                    label={t('common:browseBar.tags')}
                    value={tags}
                    options={multiSelectOptions}
                    onChange={setTags}
                    placeholder={t('persons.editModal.addTag')}
                    searchPlaceholder={t('persons.editModal.tagPlaceholder')}
                    emptyMessage={t('common:browseBar.noMatchingTags')}
                    allowCreate
                    createLabel={(value) =>
                        `${t('persons.editModal.addTag')} "${value}"`
                    }
                />
                {person?.known && (
                    <ProfilePictureSection
                        person={person}
                        pending={profilePicturePending}
                        onPendingChange={setProfilePicturePending}
                        onInProgressChange={setIsPhotoFlowInProgress}
                    />
                )}
            </div>
        </Modal>
    );
}
